import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStatusIcon } from '../../utils/helpers';
import { getStatusChipStyle } from './utils';

/**
 * ExperimentBasicInfoSection Component
 *
 * Displays basic experiment information: status, template, and description.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 */
function ExperimentBasicInfoSection({ experiment }) {
  const theme = useTheme();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescriptionExpansion = () => {
    setIsDescriptionExpanded(prev => !prev);
  };

  const shouldShowMoreButton = description => {
    if (!description) return false;
    const wordCount = description.trim().split(/\s+/).length;
    return wordCount > 30;
  };

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 0.75,
            letterSpacing: '-0.01em',
          }}
        >
          Basic Information
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CardContent sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 1.5,
              }}
            >
              {/* Status */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={experiment.status}
                  icon={getStatusIcon(experiment.status)}
                  sx={{
                    ...getStatusChipStyle(experiment.status, theme),
                    width: 110,
                    height: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': {
                      paddingLeft: '6px', // Padding between icon and text
                      paddingRight: '8px', // Right padding
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: 1,
                    },
                    '& .MuiChip-icon': {
                      marginLeft: '6px', // Left padding
                      marginRight: 0, // No right margin, padding handled by label
                      width: '16px',
                      height: '16px',
                      color: 'inherit',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                />
              </Box>

              {/* Template */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Template
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    mb: 0.5,
                    fontWeight: 500,
                    fontFamily: 'monospace',
                  }}
                >
                  {experiment.template_id || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Description
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  disabled
                  value={experiment.description || ''}
                  placeholder="No description provided"
                  rows={isDescriptionExpanded ? 20 : 4}
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor: theme.palette.background.paper,
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        '& fieldset': {
                          borderColor: theme.palette.divider,
                        },
                        '& textarea': {
                          overflow: 'hidden',
                          resize: 'none',
                        },
                      },
                    },
                    '& .MuiInputBase-input': {
                      paddingLeft: '14px',
                      paddingRight:
                        experiment.description &&
                        shouldShowMoreButton(experiment.description)
                          ? '80px'
                          : '14px',
                      paddingTop: '14px',
                      paddingBottom:
                        experiment.description &&
                        shouldShowMoreButton(experiment.description)
                          ? '32px'
                          : '14px',
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
                {experiment.description &&
                  shouldShowMoreButton(experiment.description) && (
                    <Button
                      onClick={toggleDescriptionExpansion}
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        bottom: '8px',
                        minWidth: 'auto',
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        color: theme.palette.primary.main,
                        backgroundColor: 'transparent',
                        zIndex: 1,
                        '&:hover': {
                          backgroundColor:
                            theme.palette.action.hover ||
                            'rgba(0, 122, 255, 0.1)',
                        },
                      }}
                    >
                      {isDescriptionExpanded ? 'Show less' : 'Show more'}
                    </Button>
                  )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}

export default ExperimentBasicInfoSection;
